import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Play, Loader2, Upload, ArrowLeft, Volume2, Mic2,
} from "lucide-react";
import { agentsApi, previewVoice, uploadFile, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const EMPTY = { name: "", voice_id: "", personality: "", avatar_url: "", circle_video_url: "" };

function UploadField({ label, kind, value, onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file, kind);
      onChange(res.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-white/70 text-xs uppercase tracking-wider">{label}</Label>
      <div className="flex gap-2">
        <Input
          data-testid={`${testid}-url`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload a file"
          className="bg-[#111116] border-white/10 focus:border-white/40 text-white"
        />
        <input
          ref={inputRef}
          type="file"
          accept={kind === "image" ? "image/*" : "video/*"}
          className="hidden"
          onChange={pick}
        />
        <Button
          type="button"
          data-testid={`${testid}-upload`}
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="border-white/15 text-white/80 shrink-0"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </Button>
      </div>
      {value && kind === "image" && (
        <img src={fileUrl(value)} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
      )}
    </div>
  );
}

function AgentForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [errors, setErrors] = useState({});
  const audioRef = useRef(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.voice_id.trim()) e.voice_id = "Voice ID is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doPreview = async () => {
    if (!form.voice_id.trim()) {
      setErrors((x) => ({ ...x, voice_id: "Enter a voice ID to preview" }));
      return;
    }
    setPreviewing(true);
    try {
      const url = await previewVoice(form.voice_id.trim(), "Привет, это превью моего голоса.");
      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
      }
      toast.success("Voice preview playing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed — check the voice ID");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      voice_id: form.voice_id.trim(),
      personality: form.personality || "",
      avatar_url: form.avatar_url?.trim() || null,
      circle_video_url: form.circle_video_url?.trim() || null,
    };
    try {
      if (initial?.id) await agentsApi.update(initial.id, payload);
      else await agentsApi.create(payload);
      toast.success(initial?.id ? "Agent updated" : "Agent created");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-agent-form"
        className="bg-[#0d0d12] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-light">
            {initial?.id ? "Edit agent" : "New agent"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-wider">Name</Label>
              <Input
                data-testid="agent-name-input"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                className="bg-[#111116] border-white/10 focus:border-white/40 text-white"
              />
              {errors.name && <p className="text-[#E63946] text-xs">{errors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-wider">Fish Audio Voice ID</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="agent-voice-input"
                  value={form.voice_id}
                  onChange={(e) => set("voice_id")(e.target.value)}
                  className="bg-[#111116] border-white/10 focus:border-white/40 text-white font-mono-tech text-sm"
                />
                <Button
                  type="button"
                  data-testid="voice-preview-button"
                  onClick={doPreview}
                  disabled={previewing}
                  variant="outline"
                  className="border-white/15 text-white/80 shrink-0"
                >
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                </Button>
              </div>
              {errors.voice_id && <p className="text-[#E63946] text-xs">{errors.voice_id}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-xs uppercase tracking-wider">Personality / System prompt</Label>
            <Textarea
              data-testid="agent-personality-input"
              value={form.personality}
              onChange={(e) => set("personality")(e.target.value)}
              rows={8}
              placeholder="Describe who this agent is, their tone, background…"
              className="bg-[#111116] border-white/10 focus:border-white/40 text-white resize-y"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <UploadField label="Avatar" kind="image" value={form.avatar_url} onChange={set("avatar_url")} testid="avatar" />
            <UploadField label="Circle video" kind="video" value={form.circle_video_url} onChange={set("circle_video_url")} testid="circlevideo" />
          </div>
        </div>

        <audio ref={audioRef} className="hidden" />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60">
            Cancel
          </Button>
          <Button
            data-testid="agent-save-button"
            onClick={save}
            disabled={saving}
            className="bg-[#E63946] hover:bg-[#c62f3b] text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {initial?.id ? "Save changes" : "Create agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const { data: agents = [], isLoading } = useQuery({ queryKey: ["agents"], queryFn: agentsApi.list });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["agents"] });

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (a) => { setEditing(a); setFormOpen(true); };

  const confirmDelete = async () => {
    try {
      await agentsApi.remove(toDelete.id);
      toast.success("Agent deleted");
      refresh();
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="grain min-h-screen bg-[#070709] text-white">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col w-64 min-h-screen border-r border-white/10 bg-[#0a0a0e] px-6 py-8">
          <div className="flex items-center gap-2 mb-12">
            <Mic2 className="w-5 h-5 text-[#E63946]" />
            <span className="font-serif-display text-2xl font-light">Voice Studio</span>
          </div>
          <nav className="space-y-1 text-sm">
            <div className="px-3 py-2 rounded-lg bg-white/5 text-white flex items-center gap-2">
              <Volume2 className="w-4 h-4" /> Agents
            </div>
          </nav>
          <Link
            to="/"
            data-testid="back-to-voice-link"
            className="mt-auto flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Voice UI
          </Link>
        </aside>

        {/* Main */}
        <main className="flex-1 px-6 md:px-12 py-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="font-mono-tech text-[11px] tracking-[0.3em] uppercase text-white/40 mb-2">
                Admin / Agents
              </p>
              <h1 className="font-serif-display text-4xl md:text-5xl font-light">Manage agents</h1>
            </div>
            <Button
              data-testid="new-agent-button"
              onClick={openNew}
              className="bg-[#E63946] hover:bg-[#c62f3b] text-white rounded-full px-5"
            >
              <Plus className="w-4 h-4 mr-2" /> New agent
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-white/50">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : agents.length === 0 ? (
            <div className="text-white/50 border border-dashed border-white/10 rounded-2xl p-16 text-center">
              No agents yet. Create your first persona.
            </div>
          ) : (
            <div className="border border-white/10 rounded-2xl overflow-hidden" data-testid="agent-table">
              <div className="grid grid-cols-[64px_1fr_1fr_120px] gap-4 px-6 py-4 bg-white/[0.03] text-white/40 font-mono-tech text-[11px] uppercase tracking-wider">
                <span>Avatar</span>
                <span>Name</span>
                <span>Voice ID</span>
                <span className="text-right">Actions</span>
              </div>
              {agents.map((a) => (
                <div
                  key={a.id}
                  data-testid={`agent-row-${a.id}`}
                  className="grid grid-cols-[64px_1fr_1fr_120px] gap-4 px-6 py-4 items-center border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-[#1A1A22] flex items-center justify-center">
                    {a.avatar_url ? (
                      <img src={fileUrl(a.avatar_url)} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-serif-display text-lg text-white/60">{a.name?.[0]}</span>
                    )}
                  </div>
                  <div className="font-serif-display text-xl">{a.name}</div>
                  <div className="font-mono-tech text-xs text-white/50 truncate">{a.voice_id}</div>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      data-testid={`edit-agent-${a.id}`}
                      size="icon" variant="ghost"
                      onClick={() => openEdit(a)}
                      className="text-white/60 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`delete-agent-${a.id}`}
                      size="icon" variant="ghost"
                      onClick={() => setToDelete(a)}
                      className="text-white/60 hover:text-[#E63946]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {formOpen && (
        <AgentForm
          open={formOpen}
          onOpenChange={setFormOpen}
          initial={editing}
          onSaved={refresh}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0d0d12] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl font-light">
              Delete “{toDelete?.name}”?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              This permanently removes the agent from the collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white/70">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={confirmDelete}
              className="bg-[#E63946] hover:bg-[#c62f3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
